import { useQuery } from '@tanstack/react-query';
import { ledgerService } from '../../services/ledger/ledgerService';
import type { 
  TrialBalanceResponse,
  AccountLedgerResponse,
  TrialBalanceParams,
  AccountLedgerParams
} from '../../services/ledger/ledgerService';

export interface UseLedgerReportsReturn {
  // Trial Balance
  getTrialBalance: (params: TrialBalanceParams) => Promise<TrialBalanceResponse>;
  trialBalanceLoading: boolean;
  trialBalanceError: string | null;
  
  // Account Ledger
  getAccountLedger: (accountCode: string, params?: AccountLedgerParams) => Promise<AccountLedgerResponse>;
  accountLedgerLoading: boolean;
  accountLedgerError: string | null;
  
  // Chart of Accounts
  chartOfAccounts: unknown[] | null;
  chartOfAccountsLoading: boolean;
  chartOfAccountsError: string | null;
  refreshChartOfAccounts: () => void;
  
  // Periods
  periods: unknown[] | null;
  periodsLoading: boolean;
  periodsError: string | null;
  refreshPeriods: () => void;
}

export function useLedgerReports(): UseLedgerReportsReturn {
  // Chart of Accounts query (cached, rarely changes)
  const {
    data: chartOfAccountsData,
    isLoading: chartOfAccountsLoading,
    error: chartOfAccountsError,
    refetch: refetchChartOfAccounts,
  } = useQuery({
    queryKey: ['ledger', 'chart-of-accounts'],
    queryFn: () => ledgerService.getChartOfAccounts(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true, // Ensure query is enabled
  });

  // Periods query (cached, changes infrequently)
  const {
    data: periodsData,
    isLoading: periodsLoading,
    error: periodsError,
    refetch: refetchPeriods,
  } = useQuery({
    queryKey: ['ledger', 'periods'],
    queryFn: () => ledgerService.getPeriods(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: true, // Ensure query is enabled
  });

  // Trial Balance - on-demand query
  const getTrialBalance = async (params: TrialBalanceParams): Promise<TrialBalanceResponse> => {
    return ledgerService.getTrialBalance(params);
  };

  // Account Ledger - on-demand query
  const getAccountLedger = async (
    accountCode: string, 
    params: AccountLedgerParams = {}
  ): Promise<AccountLedgerResponse> => {
    return ledgerService.getAccountLedger(accountCode, params);
  };

  return {
    // Trial Balance
    getTrialBalance,
    trialBalanceLoading: false, // On-demand, so no persistent loading state
    trialBalanceError: null,
    
    // Account Ledger
    getAccountLedger,
    accountLedgerLoading: false, // On-demand, so no persistent loading state
    accountLedgerError: null,
    
    // Chart of Accounts
    chartOfAccounts: chartOfAccountsData?.accounts || null,
    chartOfAccountsLoading,
    chartOfAccountsError: chartOfAccountsError?.message || null,
    refreshChartOfAccounts: refetchChartOfAccounts,
    
    // Periods
    periods: periodsData?.periods || null,
    periodsLoading,
    periodsError: periodsError?.message || null,
    refreshPeriods: refetchPeriods,
  };
}
